import { H3 } from "../../components/Heading";
import useGqlClient from "../../hooks/useGqlClient";
import {
  Question as GqlQuestion,
  DataRoomFile,
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  QuestionNodeType,
  DealActivityType,
  useUpdateQuestionMutation,
  QuestionStatus,
  QuestionQuery,
  useAccessControlAddMoreQuery,
  DealGroup,
  Account,
  Ddql,
  useQuestionsViewQuery,
  QuestionsViewQuery,
  PermissionsResourceType,
  PermissionOverrideEntityType,
  DealRoleType,
} from "../../graphql/generated";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { QuestionClusters } from "./clusters";
import { Button } from "../../components/tailwind/Button";
import { PlusIcon } from "@heroicons/react/20/solid";
import { AnimatedModal } from "../../components/AnimatedModal";
import { TextInput } from "../../components/tailwind/TextInput";
import { TextArea } from "../../components/tailwind/TextArea";
import { CloseIcon } from "../../components/CloseIcon";
import { toasts } from "../../components/toasts/toasts";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { classNames } from "../../utils/cn";
import { NewDDQLModal } from "./NewDDQLModal";
import Dropdown, { Option } from "../../components/tailwind/Dropdown";
import {
  ListPlusIcon,
  XCircleIcon,
  CheckIcon,
  ListIcon,
  SparklesIcon,
} from "lucide-react";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import { Menu } from "@headlessui/react";
import { AssignToDealGroupModal } from "./AssignToDealGroupModal";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { UpdateQuestionModal } from "./UpdateQuestionModal";
import { QuestionPriorityPill } from "@/src/components/QuestionPriorityPill";
import {
  formatDistanceToNowStrict,
  fromUnixTime,
  getUnixTime,
  startOfDay,
  sub,
} from "date-fns";
import { formatEnum } from "@/src/utils/enums";
import clsx from "clsx";
import { Avatar } from "@/src/components/account/Avatar";
import {
  getQuestionPriorityText,
  QuestionPriorityIcon,
} from "@/src/components/QuestionPriorityIcon";
import { ProgressBar } from "@/src/components/ProgressBar";
import {
  FilterButton,
  FilterOption,
} from "@/src/components/data_room/filters/filter_button";
import { dealRoleToDealRoleOption } from "@/src/utils/typeconv";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { Avatars } from "@/src/components/Avatars";
import TableHeader from "@/src/components/tailwind/TableHeader";
import { ClustersBanner } from "./clusters";
import { Toggle } from "@/src/components/Toggle";

const lastUpdatedFilterOptions: { value: number; label: string }[] = [
  {
    label: "Today",
    value: getUnixTime(startOfDay(new Date())),
  },
  {
    label: "Last 3 days",
    value: getUnixTime(sub(new Date(), { days: 3 })),
  },
  {
    label: "Last 7 days",
    value: getUnixTime(sub(new Date(), { days: 7 })),
  },
  {
    label: "Last 30 days",
    value: getUnixTime(sub(new Date(), { days: 30 })),
  },
];

export type QuestionsFilters = {
  createdBy?:
    | Pick<DealGroup, "id" | "name">
    | Pick<Account, "id" | "name" | "avatar">;
  list?: Pick<Ddql, "id" | "name">;
  lastUpdated?: number;
};

export type QuestionsView = "default" | "smart";

export interface QuestionsContextType {
  filters: QuestionsFilters;
  setFilters: (filters: QuestionsFilters) => void;
  view: QuestionsView;
  setView: (view: QuestionsView) => void;
}

export const QuestionsContext = createContext<QuestionsContextType>({
  filters: {},
  setFilters: () => {},
  view: "default",
  setView: () => {},
});

export function Questions() {
  const [filters, setFilters] = useState<QuestionsFilters>({});
  const [view, setView] = useState<QuestionsView>("default");
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const questionsViewQuery = useQuestionsViewQuery(client, {
    dealId,
    input: {},
  });

  return (
    <QuestionsContext.Provider
      value={{
        setFilters,
        filters,
        view,
        setView,
      }}
    >
      <div className="">
        <PermissionCheck
          input={{
            permissionKey: "deal:is_admin",
          }}
        >
          <ClustersBanner dealId={dealId} />
        </PermissionCheck>
        <Header query={questionsViewQuery} />
        {view === "default" ? (
          <Content questionsViewQuery={questionsViewQuery} />
        ) : (
          <QuestionClusters />
        )}
      </div>
    </QuestionsContext.Provider>
  );
}

function Header(props: { query: UseQueryResult<QuestionsViewQuery, unknown> }) {
  const { filters, setFilters, view, setView } = useContext(QuestionsContext);
  const [openModal, setOpenModal] = useState<
    "" | "create_ddql" | "new_question"
  >("");

  const queryClient = useQueryClient();

  const [createdByOptions, setCreatedByOptions] = useState<
    FilterOption<Pick<Account, "id" | "name" | "avatar">>[]
  >([]);

  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  function getDdqlCreatedBys(
    ddql: QuestionsViewQuery["questionsView"]["ddqls"][0],
  ): Account[] {
    let createdBys = [ddql.createdBy];
    for (const q of ddql.questions) {
      createdBys.push(q.createdBy);
    }

    return createdBys as Account[];
  }

  useEffect(() => {
    if (!props.query.data) {
      return;
    }

    const questionsCreatedBys = props.query.data.questionsView.questions.map(
      (q) => q.createdBy,
    );

    let ddqlCreatedBys = [];
    for (const ddql of props.query.data.questionsView.ddqls) {
      const cbs = getDdqlCreatedBys(ddql);
      ddqlCreatedBys.push(...cbs);
    }

    const uniqueCreatedBys = [...questionsCreatedBys, ...ddqlCreatedBys]
      .filter(
        (createdBy, index, self) =>
          index === self.findIndex((t) => t.id === createdBy.id),
      )
      .map((createdBy) => ({
        value: createdBy,
        label: createdBy.name,
      }));

    setCreatedByOptions(uniqueCreatedBys);
  }, [props.query.data]);

  return (
    <div className="w-full flex items-center justify-between p-2 pt-0">
      <div className="flex items-center gap-x-2 ">
        <FilterButton
          defaultLabel="Created by"
          options={createdByOptions}
          selected={createdByOptions.find(
            (option) => option.value === filters.createdBy,
          )}
          onChange={(value) => {
            setFilters({
              ...filters,
              createdBy: value as Pick<Account, "id" | "name" | "avatar">,
            });
          }}
          renderOption={(option) => {
            return (
              <div className="flex items-center gap-x-2">
                <Avatar account={option.value} size="xs" />
                <p className="text-sm font-semibold text-gray-500">
                  {option.label}
                </p>
              </div>
            );
          }}
        />

        {/* 
        <FilterButton
          defaultLabel="Question list"
          options={lastUpdatedFilterOptions}
          selected={lastUpdatedFilterOptions.find(
            (option) => option.value === filters.lastUpdated,
          )}
          onChange={(value) => {
            setFilters({
              ...filters,
              lastUpdated: value as number,
            });
          }}
          renderOption={(option) => {
            return (
              <div className="flex items-center gap-x-2">
                <p className="text-sm font-semibold text-gray-500">
                  {option.label}
                </p>
              </div>
            );
          }}
        />
        <FilterButton
          defaultLabel="Last updated"
          options={lastUpdatedFilterOptions}
          selected={lastUpdatedFilterOptions.find(
            (option) => option.value === filters.lastUpdated,
          )}
          onChange={(value) => {
            setFilters({
              ...filters,
              lastUpdated: value as number,
            });
          }}
          renderOption={(option) => {
            return (
              <div className="flex items-center gap-x-2">
                <p className="text-sm font-semibold text-gray-500">
                  {option.label}
                </p>
              </div>
            );
          }}
        /> */}
      </div>
      <div className="w-1/3 hidden xl:block">
        {props.query.data ? (
          <div>
            <div className="flex items-center gap-x-1.5">
              <div className="w-4/5">
                <ProgressBar
                  height="h-1.5"
                  progress={
                    (props.query.data.deal.totalAnsweredQuestions /
                      props.query.data.deal.totalQuestions) *
                    100
                  }
                  backgroundColor="bg-green-100"
                  progressColor="bg-green-500"
                />
              </div>
              <p className="text-xs text-concrete-500 leading-none">
                {props.query.data.deal.totalAnsweredQuestions} /{" "}
                {props.query.data.deal.totalQuestions}
              </p>
            </div>
          </div>
        ) : null}
      </div>
      <PermissionCheck
        input={{
          permissionKey: "questions:create",
        }}
      >
        <div className="flex items-center gap-x-2">
          <Button
            size="s"
            variant="neutral"
            icon={PlusIcon}
            text="New question"
            onClick={() => {
              setOpenModal("new_question");
            }}
          />
          <Button
            icon={ListPlusIcon}
            variant="positive"
            size="s"
            text="New list"
            onClick={() => {
              setOpenModal("create_ddql");
            }}
          />
        </div>
      </PermissionCheck>

      <NewQuestionModal
        open={openModal === "new_question"}
        onClose={() => {
          queryClient.invalidateQueries({
            queryKey: [
              "DealGroupQuestions",
              { dealId: activeDealId, input: {} },
            ],
          });
          setOpenModal("");
        }}
      />
      <NewDDQLModal
        open={openModal === "create_ddql"}
        redirectOnSuccess={true}
        onClose={() => {
          setOpenModal("");
          queryClient.invalidateQueries({
            queryKey: ["Questions", { dealId: activeDealId, input: {} }],
          });
        }}
        parentDdqlID=""
        dealFirmGroupId={""} // TODO
        upload={true}
      />
    </div>
  );
}

function Content(props: {
  questionsViewQuery: UseQueryResult<QuestionsViewQuery, unknown>;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const { questionsViewQuery } = props;

  const { filters } = useContext(QuestionsContext);

  const history = useHistory();

  if (questionsViewQuery.error) {
    return (
      <div className="p-8 flex justify-center items-center">
        <p className="text-sm font-semibold text-gray-500">
          Something went wrong
        </p>
      </div>
    );
  }

  if (questionsViewQuery.isLoading || !questionsViewQuery.data) {
    return (
      <div className="p-4 w-full">
        <div className="animate-pulse">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-x-4">
                <div className="h-12 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex gap-x-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-96"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasData =
    questionsViewQuery.data.questionsView.ddqls.length > 0 ||
    questionsViewQuery.data.questionsView.questions.length > 0;

  if (!hasData) {
    return (
      <div className="p-8 flex justify-center items-center">
        <div className="text-center">
          <p className="text-gray-600 font-semibold">No questions yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Questions will appear here once they are created
          </p>
        </div>
      </div>
    );
  }

  function filterDdqlCreatedBy(
    ddql: Ddql,
    targetCreatedBy: Pick<Account, "id" | "name" | "avatar">,
  ): boolean {
    if (ddql.createdBy.id === targetCreatedBy.id) {
      return true;
    }

    for (const question of ddql.questions) {
      if (question.createdBy.id === targetCreatedBy.id) {
        return true;
      }
    }

    for (const child of ddql.children) {
      if (filterDdqlCreatedBy(child as Ddql, targetCreatedBy)) {
        return true;
      }
    }

    return false;
  }

  const filteredDdqls = questionsViewQuery.data.questionsView.ddqls.filter(
    (ddql) => {
      if (filters.createdBy) {
        return filterDdqlCreatedBy(ddql as Ddql, filters.createdBy as Account);
      }

      return true;
    },
  );

  const filteredQuestions =
    questionsViewQuery.data.questionsView.questions.filter((q) => {
      if (filters.createdBy) {
        return q.createdBy.id === filters.createdBy.id;
      }

      return true;
    });

  return (
    <div className="">
      <div className="bg-white border-t border-concrete-100 max-h-[calc(100vh-200px)] overflow-y-scroll rounded-b-md">
        <div className="p-4 space-y-2">
          {filteredDdqls
            .sort((a, b) => {
              if (a.questions.length > 0 && b.questions.length === 0) return -1;
              if (a.questions.length === 0 && b.questions.length > 0) return 1;
              return 0;
            })
            .map((ddql) => (
              <DdqlCard ddql={ddql} />
            ))}
        </div>

        <div className="w-full h-px bg-concrete-100 my-4"></div>

        <p className="font-bold text-persian-990 px-4 text-md">
          Other questions
        </p>
        <table className="min-w-full divide-y divide-concrete-100 ">
          <thead className="">
            <tr>
              <TableHeader
                className="gap-x-2 tracking-wider flex items-center"
                padding="m l"
                style={{ width: "100%" }}
              >
                <p>Question</p>
              </TableHeader>
              <TableHeader
                padding="m l"
                className="tracking-wider whitespace-nowrap"
                align="left"
              >
                Created by
              </TableHeader>
              <TableHeader
                padding="m l"
                className="tracking-wider whitespace-nowrap"
                align="left"
              >
                Status
              </TableHeader>
              <TableHeader
                padding="m l"
                className="tracking-wider whitespace-nowrap"
                align="left"
              >
                Priority
              </TableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-concrete-100">
            {filteredQuestions.map((question) => (
              <tr
                key={question.id}
                className="hover:bg-gray-50 cursor-pointer group transition-colors duration-150"
                onClick={() => {
                  history.push(`/deal/${dealId}/questions/${question.id}`);
                }}
              >
                <td className="px-4 py-2 text-sm max-w-0 w-full">
                  <p className="truncate text-persian-990 leading-relaxed">
                    {question.title}
                  </p>
                </td>
                <td className="whitespace-nowrap px-4">
                  <div className="flex items-center gap-x-1.5 whitespace-nowrap">
                    <Avatar size="s" account={question.createdBy} />
                    <p className="text-sm text-concrete-950">
                      {question.createdBy.name}
                    </p>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4">
                  <QuestionStatusText
                    status={question.status}
                    answers={question.answers as any}
                  />
                </td>
                <td className="whitespace-nowrap px-4">
                  <div className="flex items-center gap-x-1.5 whitespace-nowrap">
                    <QuestionPriorityIcon priority={question.priority} />
                    <p className="text-sm text-concrete-950">
                      {getQuestionPriorityText(question.priority ?? null)}
                    </p>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DdqlCard(props: {
  ddql: QuestionsViewQuery["questionsView"]["ddqls"][0];
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const { ddql } = props;
  const history = useHistory();

  return (
    <div
      onClick={() => {
        history.push(`/deal/${dealId}/questions/lists/${ddql.id}`);
      }}
    >
      <div className="flex items-center p-4 rounded-md border border-concrete-100 hover:bg-persian-50 hover:border-persian-100 cursor-pointer mb-2">
        <div className="flex items-center flex-1">
          <p className="font-semibold text-persian-990 text-md">
            {props.ddql.name}
          </p>

          <div className="ml-4">
            <PermissionCheck
              input={{
                roleType: DealRoleType.Internal,
                permissionKey: "questions:manage_access",
              }}
            >
              <Avatars
                size="s"
                accounts={
                  ddql
                    ? ddql.accessControlDealGroups.flatMap((acdg) =>
                        acdg.accounts.map((da) => da.dealAccount.account),
                      )
                    : []
                }
                max={6}
              />
            </PermissionCheck>
          </div>
        </div>

        <div className="ml-4 flex items-center">
          <div className="flex items-center gap-x-1.5">
            <div className="w-32">
              <ProgressBar
                progress={
                  (ddql.answeredChildQuestions / ddql.totalChildQuestions) * 100
                }
                height="h-1"
                progressColor="bg-green-500"
                backgroundColor="bg-green-200"
              />
            </div>
            <p className="text-xs text-gray-500">
              {ddql.answeredChildQuestions} / {ddql.totalChildQuestions}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

type ExtraGroup = {
  group?: {
    id: string;
    name: string;
  };
  dealRoleId: string;
};

export function NewQuestionModal(props: {
  open: boolean;
  onClose: () => void;
  ddqlId?: string;
  forDealFirmGroupId?: string;
}) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const [files, setFiles] = useState<DataRoomFile[]>([]);
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [details, setDetails] = useState("");
  const [selectedDfg, setSelectedDfg] = useState<Option | null>(null);

  const [extraGroups, setExtraGroups] = useState<ExtraGroup[]>([]);

  const [error, setError] = useState("");

  const createButtonRef = useRef<HTMLButtonElement>(null);

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createQuestion = useCreateQuestionMutation(client);
  const history = useHistory();

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (createButtonRef.current) {
          createButtonRef.current.click();
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function clearForm() {
    setFiles([]);
    setTitle("");
    setDetails("");
    setTitleError("");
    setError("");
    setExtraGroups([]);
  }

  return (
    <AnimatedModal
      size="xl"
      position="center"
      open={props.open}
      onClose={() => {
        clearForm();
        props.onClose();
      }}
    >
      <div>
        <div className="justify-between flex items-center">
          <H3>New question</H3>
          <CloseIcon onClose={props.onClose} />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (!title) {
              setTitleError("Title is required");
              return;
            }

            let questionForDealFirmGroupId = props.forDealFirmGroupId;
            if (
              !questionForDealFirmGroupId &&
              selectedDfg &&
              selectedDfg.value
            ) {
              questionForDealFirmGroupId = selectedDfg.value;
            }

            const groups = extraGroups.filter((group) => group.group);

            if (groups.length > 0) {
              const groupsNoPermission = groups.filter(
                (group) => !group.dealRoleId,
              );

              if (groupsNoPermission.length > 0) {
                setError("Please select a permission level for each group");
                return;
              }
            }

            createQuestion.mutate(
              {
                title,
                body: details,
                nodeType: QuestionNodeType.DataRoomFile,
                nodeId: files.length > 0 ? files[0].id : "",
                ddqlID: props.ddqlId,
                dealRoleOverrides: groups
                  .filter((group) => group.group && group.dealRoleId)
                  .map((group) => ({
                    entityID: group.group!.id,
                    entityType: PermissionOverrideEntityType.DealGroup,
                    dealRoleID: group.dealRoleId,
                    resourceID: "",
                    resourceType: PermissionsResourceType.Question,
                  })),
              },
              {
                onSuccess: (data) => {
                  queryClient.invalidateQueries({
                    queryKey: ["Questions", { input: {} }],
                  });

                  if (props.ddqlId) {
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql", { id: props.ddqlId }],
                    });
                  }

                  if (props.forDealFirmGroupId) {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "DealFirmGroupQuestions",
                        { id: props.forDealFirmGroupId },
                      ],
                    });
                  }

                  props.onClose();
                  clearForm();
                  history.push({
                    pathname: `/deal/${activeDealId}/questions/${data.createQuestion.id}`,
                  });
                },
                onError: (e) => {
                  setError("Failed to create question");
                },
              },
            );

            props.onClose();
          }}
        >
          <div className="mt-4">
            <TextInput
              label="Question"
              placeholder="The question you want to ask"
              value={title}
              error={titleError}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
                setTitleError("");
              }}
            />

            <label
              htmlFor="details"
              className="block mt-3 text-sm font-semibold leading-6 text-gray-900"
            >
              Additional details
            </label>
            <TextArea
              rows={3}
              name="details"
              value={details}
              onChange={(e) => {
                setDetails(e.currentTarget.value);
              }}
              placeholder="Any extra context/details you want to add..."
            />

            {!props.ddqlId ? (
              <div className="mt-4">
                <p className="text-sm font-semibold leading-1 text-gray-900">
                  Access
                  <span className="text-xs text-gray-500 font-normal ml-1">
                    Optional
                  </span>
                </p>
                <p className="text-xs text-gray-500 mb-2">
                  Quickly add other groups. You can always add more later.
                </p>
                <ExtraGroups
                  groups={extraGroups}
                  setGroups={(groups) => {
                    setError("");
                    setExtraGroups(groups);
                  }}
                />
              </div>
            ) : null}

            {error ? (
              <p className="mt-4  text-red-500 font-semibold text-sm">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end mt-6 z-0">
              <Button
                ref={createButtonRef}
                isLoading={createQuestion.isPending}
                loadingText="Posting..."
                text="Create question"
                variant="positive"
                type="submit"
              />
            </div>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}

function ExtraGroups(props: {
  groups: ExtraGroup[];
  setGroups: (groups: ExtraGroup[]) => void;
}) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const addMoreQuery = useAccessControlAddMoreQuery(client, {
    dealId: activeDealId,
    dealRolesInput: {
      roleTypes: [],
      resourceTypes: [PermissionsResourceType.Question],
    },
  });

  if (addMoreQuery.error) {
    return <p className="text-gray-500 text-sm">Something went wrong</p>;
  }

  if (addMoreQuery.isLoading || addMoreQuery.isError || !addMoreQuery.data) {
    return null;
  }

  return (
    <div className="">
      <div className="space-y-2">
        {props.groups.map((group, index) => (
          <ExtraGroupRow
            key={index}
            index={index}
            onUpdate={(group) =>
              props.setGroups(
                props.groups.map((g, i) => (i === index ? group : g)),
              )
            }
            onRemove={() =>
              props.setGroups(props.groups.filter((_, i) => i !== index))
            }
            groupOptions={addMoreQuery.data.deal.guestGroups.map((group) => ({
              label: group.name,
              value: group.id,
            }))}
            roleOptions={addMoreQuery.data.dealRoles.map((dr) =>
              dealRoleToDealRoleOption(dr),
            )}
          />
        ))}
      </div>

      <button
        type="button"
        className="flex items-center gap-x-2 mt-2 text-sm font-semibold leading-1 text-persian-500 hover:text-persian-700"
        onClick={() => {
          props.setGroups([
            ...props.groups,
            {
              group: undefined,
              dealRoleId: "",
            },
          ]);
        }}
      >
        <PlusIcon className="w-4 h-4" />
        Add group
      </button>
    </div>
  );
}

interface ExtraGroupRowProps {
  index: number;
  onUpdate: (group: ExtraGroup) => void;
  onRemove: () => void;
  groupOptions: Option[];
  roleOptions: Option[];
}

function ExtraGroupRow(props: ExtraGroupRowProps) {
  const [selectedGroup, setSelectedGroup] = useState<Option | null>(null);
  const [roleOption, setRoleOption] = useState<Option | null>(null);

  useEffect(() => {
    if (selectedGroup) {
      props.onUpdate({
        group: {
          id: selectedGroup.value ?? "",
          name: selectedGroup.label,
        },
        dealRoleId: roleOption?.value ?? "",
      });
    }
  }, [selectedGroup, roleOption, roleOption?.value]);

  return (
    <div className="flex items-center gap-x-2 bg-gray-50 p-2 rounded-md justify-between">
      <Dropdown
        options={props.groupOptions}
        onSelect={(option) => {
          setSelectedGroup(option);
        }}
        selectedOption={{
          label: selectedGroup?.label ?? "Select group",
          value: selectedGroup?.value ?? null,
        }}
        defaultOption={{
          label: "Select group",
          value: null,
        }}
      />
      <div className="flex items-center gap-x-2">
        {selectedGroup ? (
          <Dropdown
            options={props.roleOptions}
            defaultOption={{
              label: "Select access level",
              value: null,
            }}
            selectedOption={{
              label: roleOption ? roleOption.label : "Select access level",
              value: roleOption?.value ?? null,
            }}
            onSelect={(option) => {
              setRoleOption(option);
            }}
          />
        ) : null}
        <CloseIcon
          onClose={() => {
            props.onRemove();
          }}
        />
      </div>
    </div>
  );
}

interface QuestionProps {
  question: GqlQuestion;
  isFirst: boolean;
  isLast: boolean;
  showStatus?: boolean;
  showComments?: boolean;
  showAvatar?: boolean;
  showIndex?: boolean;
  showAssignToDealGroup?: boolean;
  showPriority?: boolean;
  parentDdqls?: Array<{ id: string; name: string }>;
}

export function Question(props: QuestionProps) {
  const queryClient = useQueryClient();
  const client = useGqlClient();
  const deleteQuestion = useDeleteQuestionMutation(client);
  const showStatus = props.showStatus ?? true;
  const showComments = props.showComments ?? true;
  const showAvatar = props.showAvatar ?? true;
  const showIndex = props.showIndex ?? true;
  const showPriority = props.showPriority ?? true;
  const [openModal, setOpenModal] = useState<
    "" | "assign_to_deal_group" | "edit_question"
  >("");
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const updateQuestion = useUpdateQuestionMutation(client);
  const { invalidateQuestions } = useInvalidateQueryKeys();
  const history = useHistory();

  return (
    <div
      key={props.question.id}
      className={classNames(
        "w-full 3xl:w-4/5 hover:bg-gray-50 cursor-pointer",
        props.isFirst ? "rounded-t-md" : "",
        props.isLast ? "rounded-b-md" : "",
      )}
    >
      <div
        className="flex items-start py-2 mx-1 "
        onClick={() => {
          history.push({
            pathname: `/deal/${activeDealId}/questions/${props.question.id}`,
            state: props.parentDdqls
              ? { parentDdqls: props.parentDdqls }
              : undefined,
          });
        }}
      >
        <div className="flex px-3 py-2 items-center space-x-3 w-full justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <p className="text-gray-600 font-semibold text-sm">
                {showIndex &&
                  props.question.index != null &&
                  `${props.question.index} `}
                {props.question.title.slice(
                  0,
                  Math.min(props.question.title.length, 100),
                )}
                {props.question.title.length > 100 ? "..." : ""}
              </p>
              <p className="text-gray-500/80 text-xs">
                Asked by{" "}
                <span className="font-semibold">
                  {props.question.createdBy?.name}{" "}
                </span>
                {formatDistanceToNowStrict(
                  fromUnixTime(props.question.createdAt),
                  {
                    addSuffix: true,
                  },
                )}
              </p>
            </div>
          </div>

          <div className="flex gap-x-2 items-center">
            <div className="flex gap-x-2 items-center">
              {showPriority &&
                props.question.status !== QuestionStatus.Approved && (
                  <QuestionPriorityPill priority={props.question.priority} />
                )}

              {showComments && (
                <>
                  <CommentCountPill
                    count={
                      props.question.activity?.filter(
                        (a) => a.type === DealActivityType.Comment,
                      )?.length ?? 0
                    }
                  />
                </>
              )}
            </div>

            <QuestionStatusPill
              status={props.question.status ?? QuestionStatus.Cleared}
              answers={props.question.answers}
            />

            <PermissionCheck
              input={{
                permissionKey: "questions:edit",
                resource: {
                  resourceID: props.question.id,
                  resourceType: PermissionsResourceType.Question,
                },
              }}
            >
              <TripleDotMenu width="w-44">
                <Menu.Item>
                  {({ active, close }) => (
                    <div
                      className={classNames(
                        active ? "bg-gray-50 text-indigo-700" : "",
                        "w-full block px-3 py-1 text-sm leading-6 text-indigo-600 text-left cursor-pointer",
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        close();
                        setOpenModal("edit_question");
                      }}
                    >
                      Edit question
                    </div>
                  )}
                </Menu.Item>

                <Menu.Item>
                  {({ active, close }) => (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        close();
                        deleteQuestion.mutate(
                          {
                            input: {
                              id: props.question.id,
                            },
                          },
                          {
                            onSuccess: () => {
                              queryClient.invalidateQueries({
                                queryKey: ["Questions"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["Ddql"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["DealGroupQuestions"],
                              });
                              toasts.success("Question deleted");
                            },
                            onError: (e) => {
                              toasts.error("Failed to delete question");
                            },
                          },
                        );
                      }}
                      className={classNames(
                        active ? "bg-gray-50 text-red-700" : "",
                        "block px-3 py-1 text-sm leading-6 text-red-600 cursor-pointer",
                      )}
                    >
                      Delete question
                    </div>
                  )}
                </Menu.Item>
              </TripleDotMenu>
            </PermissionCheck>
          </div>
        </div>
      </div>
      <AssignToDealGroupModal
        open={openModal === "assign_to_deal_group"}
        onClose={() => {
          setOpenModal("");
        }}
        question={props.question}
        dealId={activeDealId ?? ""}
      />
      <UpdateQuestionModal
        open={openModal === "edit_question"}
        onClose={() => {
          setOpenModal("");
          queryClient.invalidateQueries({
            queryKey: ["Questions"],
          });
          queryClient.invalidateQueries({
            queryKey: ["Ddql"],
          });
          queryClient.invalidateQueries({
            queryKey: ["DealGroupQuestions"],
          });
          if (props.question.ddql?.id) {
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id: props.question.ddql.id }],
            });
          }
        }}
        questionId={props.question.id}
      />
    </div>
  );
}

export function QuestionStatusText(props: {
  status?: QuestionStatus | null;
  answers?: QuestionQuery["question"]["answers"] | null;
}) {
  const cn = `text-sm font-normal truncate`;

  if (!props.status) {
    return null;
  }

  if (props.status === QuestionStatus.Cleared) {
    return (
      <p
        className={clsx(
          cn,
          props.answers && props.answers.length > 0
            ? "text-blue-500"
            : "text-concrete-950",
        )}
      >
        {props.answers && props.answers.length > 0
          ? "Answer submitted"
          : "No answer"}
      </p>
    );
  }

  return (
    <p
      className={clsx(
        cn,
        props.status === QuestionStatus.Approved
          ? "text-green-600"
          : "text-red-600",
      )}
    >
      {props.status === QuestionStatus.Approved
        ? "Answer approved"
        : "Answer rejected"}
    </p>
  );
}

function QuestionStatusPill(props: {
  status: QuestionStatus;
  answers: QuestionQuery["question"]["answers"];
}) {
  if (props.status === QuestionStatus.Cleared) {
    return (
      <div className="flex flex-wrap gap-x-2 group justify-center relative">
        <div
          className={classNames(
            "cursor-pointer bg-white hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1 gap-x-1.5",
            props.answers && props.answers.length > 0
              ? " border-blue-500/50"
              : "",
          )}
        >
          <p
            className={classNames(
              " text-xs ",
              props.answers && props.answers.length > 0
                ? "text-blue-600"
                : "text-gray-600",
            )}
          >
            {props.answers && props.answers.length > 0
              ? "Answer submitted"
              : "No answer"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-x-2 group justify-center relative">
      <div
        className={classNames(
          "cursor-pointer gap-x-1.5  hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1",
          props.status === QuestionStatus.Approved
            ? "bg-green-50 border-green-500/20"
            : props.status === QuestionStatus.Rejected
              ? "bg-red-50 border-red-500/20"
              : "",
        )}
      >
        <p
          className={classNames(
            "text-gray-600 text-xs truncate",
            props.status === QuestionStatus.Approved
              ? "text-green-700"
              : "text-red-700",
          )}
        >
          Answer {formatEnum(props.status)}
        </p>
        {props.status === QuestionStatus.Approved && (
          <CheckIcon className="w-3 h-3 text-green-700" />
        )}
        {props.status === QuestionStatus.Rejected && (
          <XCircleIcon className="w-3 h-3 text-red-700" />
        )}
      </div>
    </div>
  );
}

function CommentCountPill(props: { count: number }) {
  if (!props.count) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-x-2 group justify-center relative">
      <div
        className={classNames(
          "cursor-pointer bg-white hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1",
        )}
      >
        <ChatBubbleLeftIcon className="mt-0.5 w-3 h-3 mr-1 text-gray-500" />
        <p className="text-gray-600 text-xs">
          {props.count} comment{props.count === 1 ? "" : "s"}
        </p>
      </div>
    </div>
  );
}
